import { type Ref, ref, watch } from 'vue'

import {
  type AdvertiserReport,
  type AdvertiserReportDetail,
  type AdvertiserReportDetailRaw,
  type AdvertiserReportRaw,
  type AdvertiserReportStatus,
  normalizeAdvertiserReportDetail,
  normalizeAdvertiserReports,
} from '@/lib/adapters/advertiser-report.adapter'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import { useErrorStore } from '@/stores/error'

type CreateAdvertiserReportPayload = {
  name: string
  locale: string
  store_ids: number[]
  pages: { input: string }[]
  // Optional default reporting window. Send both or neither (the API returns 422
  // otherwise); the range picker in the create form enforces that client-side.
  default_period_start?: string
  default_period_end?: string
}

type CreateAdvertiserReportResponse = {
  uuid: string
}

type AddAdvertiserReportPagePayload = {
  input: string
  position?: number
}

export const useGetAdvertiserReports = (storeIdRef: Ref<number>) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const data = ref<AdvertiserReport[]>([])
  const errorStore = useErrorStore()

  const refresh = async () => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.get<{ data: AdvertiserReportRaw[] }>(
        `/v2/claude/stores/${storeIdRef.value}/advertiser-reports?size=100&page=1`,
      )
      data.value = normalizeAdvertiserReports(response.data?.data ?? [])
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watch(storeIdRef, refresh, { immediate: true })

  return { data, isLoading, error, refresh }
}

export const useGetAdvertiserReport = (uuidRef: Ref<string>) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const data = ref<AdvertiserReportDetail | null>(null)
  const errorStore = useErrorStore()

  const refresh = async () => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.get<AdvertiserReportDetailRaw>(
        `/v2/claude/advertiser-reports/${uuidRef.value}`,
      )
      data.value = normalizeAdvertiserReportDetail(response.data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watch(uuidRef, refresh, { immediate: true })

  return { data, isLoading, error, refresh }
}

export const useCreateAdvertiserReport = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const createReport = async (payload: CreateAdvertiserReportPayload): Promise<string | null> => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.post<CreateAdvertiserReportResponse>(
        '/v2/claude/advertiser-report',
        payload,
      )
      return response.data.uuid
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return null
    } finally {
      isLoading.value = false
    }
  }

  return { createReport, isLoading, error }
}

export const useAddAdvertiserReportPage = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const addPage = async (
    reportUuid: string,
    payload: AddAdvertiserReportPagePayload,
  ): Promise<boolean> => {
    try {
      isLoading.value = true
      error.value = null
      await request.post(`/v2/claude/advertiser-reports/${reportUuid}/pages`, payload)
      return true
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return false
    } finally {
      isLoading.value = false
    }
  }

  return { addPage, isLoading, error }
}

export const useUpdateAdvertiserReportPage = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const updatePage = async (pageId: number, input: string): Promise<boolean> => {
    try {
      isLoading.value = true
      error.value = null
      await request.put<{ id: number; status: AdvertiserReportStatus }>(
        `/v2/claude/advertiser-report-pages/${pageId}`,
        { input },
      )
      return true
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return false
    } finally {
      isLoading.value = false
    }
  }

  return { updatePage, isLoading, error }
}

export const useDeleteAdvertiserReportPage = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const deletePage = async (pageId: number): Promise<boolean> => {
    try {
      isLoading.value = true
      error.value = null
      await request.delete(`/v2/claude/advertiser-report-pages/${pageId}`)
      return true
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return false
    } finally {
      isLoading.value = false
    }
  }

  return { deletePage, isLoading, error }
}
